import { supabase } from '../../../lib/supabase';
import {
    resolveClickTimeForPurchaseWindow,
    resolveMetaPurchaseRoute,
    evaluateCtwaPurchaseResult,
    type MetaAttribution,
} from './metaAttributionService';
import {
    getAttributionByLeadId,
    fetchConnectedIntegration,
    resolveMetaDatasetId,
    markAttributedConversion,
    markOfflineConversion,
    type AttributionResult,
} from './metaAttributionLookup';
import { resolveMetaWabaId } from '../metaAPI';

export interface ConversionResult {
    success: boolean;
    error?: string;
    events_received?: number;
    attributed?: boolean;
}

type CAPIPayload = {
    eventName: string;
    phone?: string;
    datasetId: string;
    organizationId: string;
    pageId?: string | null;
    wabaId?: string | null;
    ctwaClid?: string;
    igSid?: string;
    instagramBusinessAccountId?: string;
    value?: number;
    currency?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    eventId?: string;
    externalId?: string;
};

type MetaConversionsErrorBody = {
    error?: string;
    success?: boolean;
    data?: { events_received?: number; attributed?: boolean };
};

const fail = (error: string, attributed?: boolean): ConversionResult =>
    attributed === undefined ? { success: false, error } : { success: false, error, attributed };

async function triggerCAPI(payload: CAPIPayload): Promise<ConversionResult> {
    const body: Record<string, unknown> = {
        event_name: payload.eventName,
        dataset_id: payload.datasetId,
        organization_id: payload.organizationId,
        value: payload.value,
        currency: payload.currency,
        first_name: payload.firstName,
        last_name: payload.lastName,
        email: payload.email,
        event_id: payload.eventId,
    };
    if (payload.phone?.trim()) body.phone = payload.phone.trim();
    if (payload.pageId) body.page_id = payload.pageId;
    if (payload.wabaId) body.waba_id = payload.wabaId;
    for (const [src, key] of [
        [payload.ctwaClid, 'ctwa_clid'],
        [payload.igSid, 'ig_sid'],
        [payload.instagramBusinessAccountId, 'instagram_business_account_id'],
        [payload.externalId, 'external_id'],
    ] as const) {
        if (src?.trim()) body[key] = src.trim();
    }

    const { data, error } = await supabase.functions.invoke('meta-conversions', { body });
    if (error) {
        let message = error.message;
        const ctx = (error as { context?: Response }).context;
        if (ctx) {
            try {
                const errBody = (await ctx.json()) as MetaConversionsErrorBody;
                if (errBody.error) message = errBody.error;
                if (errBody.success && (errBody.data?.events_received ?? 0) > 0) {
                    return { success: true, events_received: errBody.data?.events_received };
                }
            } catch { /* keep message */ }
        }
        console.error(`[MetaCAPI] Erro ao enviar ${payload.eventName}:`, message);
        return fail(message);
    }
    if (!data?.success) return fail(data?.error || 'Erro desconhecido na Edge Function');
    console.log(`[MetaCAPI] ✅ ${payload.eventName}:`, data.data?.events_received);
    return { success: true, events_received: data.data?.events_received, attributed: data.data?.attributed };
}

/** Envio genérico à edge meta-conversions (Purchase, LeadSubmitted, marcos de funil). */
export async function sendMetaConversionEvent(payload: CAPIPayload): Promise<ConversionResult> {
    return triggerCAPI(payload);
}

function purchaseBase(
    attr: AttributionResult,
    organizationId: string,
    value: number,
    currency: string,
    eventId: string,
    phone?: string
): Omit<CAPIPayload, 'datasetId'> {
    return {
        eventName: 'Purchase',
        phone: phone?.trim() || undefined,
        organizationId,
        value,
        currency,
        firstName: attr.firstName,
        lastName: attr.lastName,
        email: attr.email,
        eventId,
        externalId: attr.contactId,
    };
}

async function tryInstagramPurchase(
    attr: AttributionResult,
    a0: MetaAttribution,
    leadId: string,
    value: number,
    currency: string,
    organizationId: string
): Promise<ConversionResult | 'next'> {
    const igSid = a0.ig_sid ?? null;
    const igBusinessId = a0.instagram_business_account_id ?? null;
    if (!igSid || !igBusinessId || !attr.conversationId || a0.conversion_attributed) return 'next';

    const igInteg = await fetchConnectedIntegration(organizationId, 'instagram_dm');
    const igDatasetId =
        (igInteg?.config?.dataset_id as string | undefined) ||
        a0.dataset_id ||
        null;
    if (!igDatasetId) return fail('Integração Instagram sem dataset_id — reconecte Meta Ads (instagram_manage_events) e o Instagram');

    const result = await triggerCAPI({
        ...purchaseBase(attr, organizationId, value, currency, leadId, attr.phone),
        igSid,
        instagramBusinessAccountId: igBusinessId,
        datasetId: igDatasetId,
    });
    if (result.success && (result.events_received ?? 0) > 0) {
        await markAttributedConversion(attr.conversationId, organizationId, a0, value);
        return { ...result, attributed: true };
    }
    return result.success ? 'next' : result;
}

async function tryCtwaPurchase(
    attr: AttributionResult,
    a0: MetaAttribution,
    metaInteg: Awaited<ReturnType<typeof fetchConnectedIntegration>>,
    datasetId: string,
    ctwaClid: string | null,
    leadId: string,
    value: number,
    currency: string,
    organizationId: string
): Promise<ConversionResult> {
    if (!attr.conversationId || !ctwaClid) {
        return fail('ctwa_clid presente mas sem conversa para Purchase CTWA');
    }
    const pageId = a0.page_id || (metaInteg?.config?.page_id as string | undefined) || null;
    const result = evaluateCtwaPurchaseResult(
        await triggerCAPI({
            ...purchaseBase(attr, organizationId, value, currency, leadId, attr.phone!),
            ctwaClid,
            datasetId,
            pageId,
            wabaId: await resolveMetaWabaId(pageId, a0, metaInteg),
        })
    );
    if (result.success && result.attributed) {
        await markAttributedConversion(attr.conversationId, organizationId, a0, value);
    }
    return result;
}

async function tryOfflinePurchase(
    attr: AttributionResult,
    a0: MetaAttribution,
    datasetId: string,
    route: string,
    leadId: string,
    value: number,
    currency: string,
    organizationId: string,
    capturedAt: string | null,
    asOfMs: number
): Promise<ConversionResult> {
    if (a0.conversion_offline_sent) return fail('Purchase offline já enviado');

    const clickAt = resolveClickTimeForPurchaseWindow({
        capturedAt,
        firstTouchAt: attr.firstTouchAt,
        asOfMs,
    });
    console.log('[MetaEvents] Purchase offline', { leadId, route, clickAt, capturedAt });

    const offlineEventId = a0.conversion_attributed ? `${leadId}-offline` : leadId;
    const result = await triggerCAPI({
        ...purchaseBase(attr, organizationId, value, currency, offlineEventId, attr.phone!),
        datasetId,
    });
    const received = result.events_received ?? 0;
    if (result.success && received > 0) {
        if (attr.conversationId) {
            await markOfflineConversion(attr.conversationId, organizationId, a0, value);
        }
        return { ...result, attributed: false };
    }
    return result.success
        ? fail('Meta não contabilizou o Purchase offline', false)
        : result;
}

/** Purchase no ganho — Instagram Direct, CTWA ≤7d ou offline. */
export async function sendPurchaseEvent(
    leadId: string,
    value: number,
    organizationId: string,
    currency = 'BRL'
): Promise<ConversionResult> {
    try {
        const attr = await getAttributionByLeadId(leadId, organizationId);
        const a0 = attr.attribution ?? ({} as MetaAttribution);

        const ig = await tryInstagramPurchase(attr, a0, leadId, value, currency, organizationId);
        if (ig !== 'next') return ig;

        if (!attr.phone) return fail(attr.error || 'Lead sem telefone');

        const metaInteg = await fetchConnectedIntegration(organizationId, 'meta_ads');
        const datasetId = resolveMetaDatasetId(a0, metaInteg);
        if (!datasetId) return fail('Sem dataset_id ou pixel_id');

        const ctwaClid = a0.ctwa_clid ?? null;
        const capturedAt = a0.captured_at ?? null;
        const asOfMs = attr.wonAt ? new Date(attr.wonAt).getTime() : Date.now();
        const route = resolveMetaPurchaseRoute({
            ctwaClid,
            igSid: a0.ig_sid ?? null,
            capturedAt,
            firstTouchAt: attr.firstTouchAt,
            firstTouchSource: attr.firstTouchSource,
            origin: attr.origin,
            asOfMs,
        });
        if (route === 'skip') return fail('Sem impacto de anúncio comprovado — Purchase não enviado');

        if (route === 'ctwa' && !a0.conversion_attributed) {
            return tryCtwaPurchase(attr, a0, metaInteg, datasetId, ctwaClid, leadId, value, currency, organizationId);
        }

        return tryOfflinePurchase(attr, a0, datasetId, route, leadId, value, currency, organizationId, capturedAt, asOfMs);
    } catch (error: unknown) {
        console.error('[MetaEvents] sendPurchaseEvent:', error);
        return fail(error instanceof Error ? error.message : 'Erro desconhecido');
    }
}

export type LeadSubmittedEventOpts = {
    eventId?: string;
    externalId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
};

export async function sendLeadSubmittedEvent(
    phone: string,
    ctwaClid: string,
    datasetId: string,
    pageId: string | null,
    organizationId: string,
    wabaId?: string | null,
    opts?: LeadSubmittedEventOpts
): Promise<ConversionResult> {
    return triggerCAPI({
        eventName: 'LeadSubmitted',
        phone: phone.replace(/\D/g, ''),
        ctwaClid,
        datasetId,
        pageId,
        wabaId,
        organizationId,
        eventId: opts?.eventId,
        externalId: opts?.externalId,
        email: opts?.email,
        firstName: opts?.firstName,
        lastName: opts?.lastName,
    });
}
